import { useState } from 'react';
import type { ClientMembership } from '@salonomy/types';
import { Button, Modal, Pill, Spinner } from '@salonomy/ui';
import { useCancelMembership, useClientMemberships } from '../../data/memberships.js';
import { ApiClientError } from '../../lib/api.js';
import { useToast } from '../../lib/toast.js';
import {
  benefitLabel,
  isMembershipLive,
  isSyntheticMembership,
  membershipDate,
  membershipLabel,
  membershipTone,
} from '../till/membershipFormat.js';

/**
 * WS-13 Feature 3b — the RECURRING-MEMBERSHIP surface on the client record. Lists this client's
 * memberships (status, next renewal / period end, member benefit) with a one-click END-OF-PERIOD cancel.
 * Mirrors LoyaltyBalanceStrip (a glanceable strip on the profile) for reads, and the WS-10 one-click SaaS
 * cancel for the cancel UX.
 *
 * HONESTY (ADR-0009): every figure is a SERVER figure the strip only DISPLAYS — the status / renewal date /
 * cancel-scheduled flag / member benefit. Nothing is computed in JS. A SYNTHETIC membership (the fake
 * gateway never minted a Stripe subscription — `stripeSubscriptionId` is null) is labelled "no charge
 * taken (test mode)" so an active membership never implies a real recurring charge is running on staging.
 *
 * BRAND RULE (one-click cancel, no retention friction): cancelling is a SINGLE confirm, effective
 * END-OF-PERIOD — NO retention flow, NO discount-to-stay, NO reason field. The benefit keeps working until
 * the period end. This mirrors the WS-10 SaaS cancel that marketing screenshots.
 *
 * Renders NOTHING when the client holds no memberships, so a client who never bought one shows no empty
 * widget — exactly like the loyalty strip.
 */
export function MembershipStrip({ clientId }: { clientId: string }) {
  const { data: memberships = [], isLoading, isError } = useClientMemberships(clientId);
  const [confirming, setConfirming] = useState<ClientMembership | null>(null);

  // Quiet while the first load resolves: don't flash an empty/loading widget on a profile that has none.
  if (isLoading) {
    return (
      <div className="client-detail__block">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size={14} />
          <span className="muted" style={{ fontSize: 12.5 }}>
            Loading memberships…
          </span>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="client-detail__block">
        <span className="slm-stat__label">Memberships</span>
        <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
          Couldn&rsquo;t load this client&rsquo;s memberships.
        </div>
      </div>
    );
  }

  // No membership → no widget (a client who never bought one shows nothing).
  if (memberships.length === 0) return null;

  return (
    <div className="client-detail__block">
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
      >
        <span className="slm-stat__label">Memberships</span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
        {memberships.map((m) => (
          <MembershipRow key={m.id} membership={m} onCancel={() => setConfirming(m)} />
        ))}
      </div>

      {confirming ? (
        <CancelMembershipDialog membership={confirming} onClose={() => setConfirming(null)} />
      ) : null}
    </div>
  );
}

/** One membership row: name-less (the strip is per-client) status + benefit + renewal/cancel line. */
function MembershipRow({
  membership,
  onCancel,
}: {
  membership: ClientMembership;
  onCancel: () => void;
}) {
  const live = isMembershipLive(membership);
  const benefit = benefitLabel(membership.benefitDiscountBps);
  const synthetic = isSyntheticMembership(membership);
  // The cancel is offered only while the membership is live and not already scheduled to end.
  const canCancel = live && !membership.cancelAtPeriodEnd;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        borderBottom: '1px solid var(--slm-border)',
        paddingBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <Pill tone={membershipTone(membership.status)}>{membershipLabel(membership.status)}</Pill>
        {membership.cancelAtPeriodEnd ? <Pill tone="soft">Ending</Pill> : null}
        {benefit ? <span style={{ fontSize: 13 }}>{benefit}</span> : null}
      </div>

      <div className="muted" style={{ fontSize: 12.5, lineHeight: 1.5 }}>
        {membership.cancelAtPeriodEnd ? (
          <>Ends {membershipDate(membership.currentPeriodEnd)} — benefit applies until then.</>
        ) : membership.currentPeriodEnd ? (
          <>Renews {membershipDate(membership.currentPeriodEnd)}.</>
        ) : (
          <>Renewal date confirms after the first billing cycle.</>
        )}
        {synthetic && live ? <> No charge taken (test mode).</> : null}
      </div>

      {canCancel ? (
        <div>
          <Button variant="outline" size="sm" onClick={onCancel}>
            Cancel membership
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * ONE-CLICK CANCEL (the brand rule): a SINGLE confirm, effective END-OF-PERIOD. NO retention flow, NO
 * discount-to-stay, NO reason field — the confirm button is the whole interaction. Double-submit is guarded
 * by the mutation's pending state; a real API error surfaces faithfully.
 */
function CancelMembershipDialog({
  membership,
  onClose,
}: {
  membership: ClientMembership;
  onClose: () => void;
}) {
  const toast = useToast();
  const cancel = useCancelMembership();

  async function confirm() {
    try {
      const cm = await cancel.mutateAsync(membership.id);
      toast.success(
        cm.currentPeriodEnd
          ? `Membership will end on ${membershipDate(cm.currentPeriodEnd)}.`
          : 'Membership set to end at the end of the current period.',
      );
      onClose();
    } catch (err) {
      toast.error(
        err instanceof ApiClientError ? err.message : 'Could not cancel — please try again.',
      );
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Cancel membership"
      width={440}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={cancel.isPending}>
            Keep membership
          </Button>
          <Button variant="danger" onClick={confirm} disabled={cancel.isPending}>
            {cancel.isPending ? 'Cancelling…' : 'Cancel membership'}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13.5, lineHeight: 1.55 }}>
        <p style={{ margin: '0 0 10px' }}>
          That&rsquo;s it — one click. The membership stays active until the end of the current paid
          period, then stops. No notice period, no cancellation fee.
        </p>
        <p style={{ margin: 0 }}>
          {membership.currentPeriodEnd ? (
            <>
              The member keeps the benefit until{' '}
              <strong>{membershipDate(membership.currentPeriodEnd)}</strong>.
            </>
          ) : (
            <>The member keeps the benefit until the end of the current period.</>
          )}
        </p>
      </div>
    </Modal>
  );
}
